import { useEffect, useMemo } from "react";
import { Select, SelectProps } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import { observer } from "mobx-react";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { getGroupListByType, GroupType } from "utils/groupHelpers";

const DISABLED_GROUP_ID = -1;
const ALL_USERS_ID = -108;

export interface GroupSelectProps extends SelectProps<number> {
  groupType: GroupType;
  withDisabledGroup?: boolean;
  withAllUsers?: boolean;
  noPrivate?: boolean;
}

export const GroupSelect: React.FC<GroupSelectProps> = observer(
  ({ groupType, withDisabledGroup, withAllUsers, noPrivate, showSearch, ...selectProps }) => {
    const { t } = useTranslation(I18N_NS.PROJECT);

    useEffect(() => {
      if (groupListStore.state === "RAW") {
        groupListStore.fetch();
      }
    }, []);

    const options = useMemo(() => {
      const { groupList: rawList, idKey } = getGroupListByType(groupType);

      let groupList = rawList.filter((group) => group.id !== ALL_USERS_ID);

      if (noPrivate) {
        groupList = groupList.filter((group) => group.type !== "PRIVATE");
      }

      const result: SelectProps<number>["options"] = [];

      if (withAllUsers) {
        result.push({ label: t("All users"), value: ALL_USERS_ID });
      }
      if (withDisabledGroup) {
        result.push({ label: t("Disabled"), value: DISABLED_GROUP_ID });
      }

      for (const item of groupList) {
        result.push({ label: item.name, value: item[idKey] });
      }

      return result;
    }, [groupType, noPrivate, withAllUsers, withDisabledGroup, t]);

    const mergedShowSearch = useMemo(() => {
      if (showSearch === false) return false;

      const externalConfig = typeof showSearch === "object" ? showSearch : {};

      return {
        filterOption: (input: string, option?: DefaultOptionType) => {
          if (!option?.label) return false;
          return String(option.label).toLowerCase().includes(input.toLowerCase());
        },
        ...externalConfig,
      };
    }, [showSearch]);

    return (
      <Select
        {...selectProps}
        showSearch={mergedShowSearch}
        options={options}
        loading={groupListStore.state === "LOADING"}
      />
    );
  }
);
